import axios from "axios";
import {
  addPaymentHistory,
  addPendingPayment
} from "../app/slice/paymentsSlice";
import { silentHTTPCaller, toastWrapper } from "../utils/toastWrapper";
import { appDispatch } from '../app/store';
import getBaseURL from "../utils/getBaseURL";
import { paramBuilder } from "../utils/common";

const url = getBaseURL() as string;

interface CreateScheduleApiBody {
  scheduleId: string,
  period: string,
  year: string,
  months: string[],
  employerCode: string,
  fileBase64String: string,
  fileExt: string,
  beneficiaries:
  {
    employerCode: string,
    year: number,
    month: string,
    employeeName: string,
    normalContributionEmployee: number,
    voluntaryContributionEmployee: number,
    normalContributionEmployer: number,
    otherContribution: number,
    voluntaryContributionEmployer: number,
    pfaCode: string,
    rsapin: string,
    staffId: string,
    totalAmount: string,
    validationMessage: string
  }[],
  saveBeneficiaries?: true,
  saveBeneficiaryAs?: string,
  beneficiaryListId?: number,
  paymentOption?: "DirectDebit" | "Wallet" | "Card",
  sourceAccountId?: 0,
  fileName?: string
}

export const getPaymentHistory = (period?: Record<string, string>, isSilent = false) => {
  const params = period ? `?fromDate=${period.fromDate}&toDate=${period.toDate}` : ''
  const call = axios.get(`${url}api/v1/pension/payment/history${params}`)

  if (isSilent) {
    silentHTTPCaller(call, 'loading payment history...', (resp) => {
      appDispatch(addPaymentHistory(resp.data.data))
      return resp.data.message || '';
    }, 'Error loading payment history');
  } else {
    toastWrapper(call, 'loading payment history...', (resp) => {
      appDispatch(addPaymentHistory(resp.data.data))
      return resp.data.message || '';
    }, 'Error loading payment history');
  }
}

export const getPFABeneficiaries = (id, page, pageCount, handleDone, isSilent) => {

  const call = axios.get(`${url}api/v1/pension/Payment/${id}/pfa/items?page=${page}&pageSize=${pageCount}`)
  if (isSilent) {
    silentHTTPCaller(call, '', (resp) => {
      handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error loading employees!');
  } else {
    toastWrapper(call, 'Loading employees...', (resp) => {
      handleDone(resp.data);
      return resp.data.message || 'Employees loaded successfully!';
    }, 'Error loading Employees!');
  }
}

export const getPayments = (id, handleDone) => {
  const call = axios.get(`${url}Payment/${id}/items`)
  silentHTTPCaller(call, '', (resp) => {
    handleDone(resp.data);
    return resp.data.message || '';
  }, 'Error loading payments');
}

export const createSchedule = (data, handleDone) => {
  const call = axios.post(`${url}api/v1/pension/Payment/schedule/create`, data)
  toastWrapper(call, 'Creating payment schedule...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Payment schedule created successfully!';
  }, 'Error creating schedule!');
}

export const uploadScheduleFile = (
  data: { ScheduleFile: File | null, FileName?: string, SaveBeneficiaryAs?: string },
  handleDone?: (data: any) => void,
  handleError?: () => void,
) => {
  const formData = new FormData();
  if (data.ScheduleFile) {
    formData.append('ScheduleFile', data.ScheduleFile);
  }
  if (data.FileName) {
    formData.append('FileName', data.FileName);
  }
  if (data.SaveBeneficiaryAs) {
    formData.append('SaveBeneficiaryAs', data.SaveBeneficiaryAs);
  }

  const call = axios.post(`${url}api/v1/pension/Payment/schedule/file/upload`, formData);
  toastWrapper(call, 'Uploading schedule, please wait...', (resp) => {
    handleDone && handleDone(resp.data);
    return resp.data.message || 'Schedule uploaded!';

  }, 'Schedule upload failed', undefined, handleError);
}

export const checkScheduleFileUploadStatus = (referenceId, handleDone, isSilent = true) => {
  const call = axios.get(`${url}api/v1/pension/Payment/schedule/file/upload/${referenceId}/status`);

  if (isSilent) {
    silentHTTPCaller(call, '', (resp) => {
      handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error checking schedule upload status', (error: any) => {
      handleDone(error?.response?.data ?? { succeed: false, message: 'Upload reference not found' });
      return error?.response?.data?.message || 'Error checking schedule upload status';
    });
  } else {
    toastWrapper(call, 'Checking schedule upload status...', (resp) => {
      handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error checking schedule upload status');
  }
}

export const removeItemFromSchedule = (beneficiaryId, scheduleId, handleDone) => {
  const call = axios.post(`${url}api/v1/pension/Payment/schedule/items/remove`, {
    scheduleId,
    beneficiaryId
  });
  toastWrapper(call, 'Removing item from schedule...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Item removed successfully!';
  }, 'Error removing item!');
}

export const addItemToSchedule = (data, handleDone) => {
  const call = axios.put(`${url}api/v1/pension/Payment/schedule/items/add`, data)
  toastWrapper(call, 'Adding item(s) to schedule...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Item(s) added successfully!';
  }, 'Error adding item!');
}

export const pfaList = (handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/pfa/list`)
  silentHTTPCaller(call, '', (resp) => {
    handleDone(resp.data.data);
    return resp.data.message || '';
  }, 'Error getting PFAs!');
}
export const loudPfaListGetter = (handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/pfa/list`)
  toastWrapper(call, 'Getting PFA data...', (resp) => {
    handleDone(resp.data.data);
    return resp.data.message || '';
  }, 'Error getting PFAs!');
}

export const getEmployerInfo = (employerCode, handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/employer/${employerCode}/info`)
  silentHTTPCaller(call, '', (resp) => {
    handleDone(resp.data);
    return resp.data.message || '';
  }, 'Error getting employer\'s info');
}

export const getPFAInfo = (pfaCode, handleDone) => {
  const call = axios.get(`${url}Payment/pfainfo/${pfaCode}`)
  silentHTTPCaller(call, '', (resp) => {
    handleDone(resp.data);
    return resp.data.message || '';
  }, 'Error getting employer\'s info');
}

export const getSchedule = (id, handleDone, isSilent = false, errorCb?: () => void) => {
  const call = axios.get(`${url}api/v1/pension/Payment/schedule/${id}`)
  if (isSilent) {
    silentHTTPCaller(call, 'loading payments...', (resp) => {
      handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error loading payments', undefined, errorCb);
  } else {
    toastWrapper(call, 'loading payments...', (resp) => {
      handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error loading payments');
  }
}

export const confirmSchedule = (scheduleId, handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/confirm/${scheduleId}`)
  toastWrapper(call, 'Confirming schedule', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Schedule confirmation successfully';
  }, 'Error confirming schedule');
}

export const validatePaymentOTP = (data: {
  otp?: string,
  paymentReference?: string,
  scheduleId: string,
  otherDetails?: string
}, handleDone) => {
  const call = axios.post(`${url}api/v1/pension/Payment/validate`, data)
  toastWrapper(call, 'Validating schedule', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Schedule validated successfully';
  }, 'Error validating schedule');
}

export const validatePayment = (id, handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/schedule/validate/${id}`)
  toastWrapper(call, 'Validating schedule', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Schedule validated successfully';
  }, 'Error validating schedule', undefined, () => {
    handleDone({});
  });
}

export const reValidateCardPayment = (id, handleDone) => {
  const call = axios.get(`${url}api/v1/pension/Payment/schedule/${id}/verify/card-payment`)
  toastWrapper(call, 'Validating schedule', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Schedule validated successfully';
  }, 'Error validating schedule');
}

export const updateScheduleBeneficiary = (data, handleDone) => {
  const call = axios.put(`${url}api/v1/pension/Payment/schedule/update/item`, data)
  toastWrapper(call, 'Updating beneficary...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Beneficary updated successfully!';
  }, 'Error updating beneficary!');
};

export const uploadEvidenceOfPayment = (data, handleDone) => {
  const call = axios.post(`${url}api/v1/Payment/offline/docs/upload`, data)
  toastWrapper(call, 'Uploading document...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Document uploaded successfully';
  }, 'Error uploading document!');
};

export const downloadEvidenceOfPayment = (itemId, handleDone) => {
  const call = axios.get(
    `${url}api/v1/Payment/offline/doc/download/${itemId}`,
    {
      responseType: 'arraybuffer'
    }
  )
  silentHTTPCaller(call, '', (resp) => {
    handleDone(resp.data, resp.headers['content-type']);
    return resp.data.message || '';
  }, 'No document available!');
};


export const deleteRemitance = (id, handleDone) => {
  const call = axios.delete(`${url}api/v1/pension/Payment/schedule/delete/${id}`);
  toastWrapper(call, 'Deleting remittance...', (resp) => {
    handleDone(resp.data);
    return resp.data.message || 'Remittance deleted successfully!';
  }, 'Error deleting remittance!');
}

///api/v1/pension/Payment/schedule/12/beneficiaries?page=1&pageSize=500
export const getPeymentScheduleBeneficiaries = (
  { scheduleId, page = 1, pageSize = 500, handleDone, isSilent = true }: {
    scheduleId: string,
    page?: number,
    pageSize?: number,
    handleDone?: (data: any) => void
    isSilent?: Boolean
  }) => {
  const endpoint = `${url}api/v1/pension/Payment/schedule/${scheduleId}/beneficiaries`
  const endpointWithQueryParams = paramBuilder(endpoint, { page, pageSize })
  const call = axios.get(endpointWithQueryParams)

  if (isSilent) {
    silentHTTPCaller(call, 'loading employees...', (resp) => {
      handleDone && handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error loading employees');
  } else {
    toastWrapper(call, 'loading employees...', (resp) => {
      handleDone && handleDone(resp.data);
      return resp.data.message || '';
    }, 'Error loading employees');
  }
}
// Approval APIs
export const getPendingPayents = ({ isSilent = false, handleDone }: { isSilent?: boolean, handleDone?: (data: any) => void }) => {
  const call = axios.get(`${url}api/v1/Approval/transactions`)
  if (isSilent) {
    silentHTTPCaller(call, 'loading pending payment...', (resp) => {
      appDispatch(addPendingPayment(resp.data.data))
      handleDone && handleDone(resp.data.data);
      return resp.data.message || '';
    }, 'Error loading pending payment');
  } else {
    toastWrapper(call, 'loading pending payment...', (resp) => {
      appDispatch(addPendingPayment(resp.data.data))
      handleDone && handleDone(resp.data.data);
      return resp.data.message || '';
    }, 'Error loading pending payment');
  }
}


export const getPaymentApprovalOtp = ({ scheduleId, handleDone }: { scheduleId?: string, handleDone?: (data: any) => void }) => {
  if (!scheduleId) {
    console.error('Schedule ID is required for approval OTP');
    return;
  }
  const call = axios.get(`${url}api/v1/Approval/transaction/confirm/${scheduleId}`)

  toastWrapper(call, 'Processing Approval...', (resp) => {
    handleDone && handleDone(resp.data);
    return resp.data.message || 'Approval Processed, Confirmetion OTP Sent Successfully.';
  }, 'Error processing approval');
}


export const approvePaymentOtp = (
  data: {
    otp: string,
    paymentReference: string,
    scheduleId: string,
    otherDetails: {
      approve: boolean,
      reason?: string
    }
  },
  handleDone?: (data: any) => void
) => {
  const call = axios.put(`${url}api/v1/Approval/transaction/confirm`, data)
  toastWrapper(call, 'Approving payment...', (resp) => {
    handleDone && handleDone(resp.data);
    return resp.data.message || 'Payment approved successfully!';
  }, 'Error approving payment');
}

export const getPaymentSummary = (
  period: string,
  year: string,
  employerCode: string,
  handleDone: (data: any) => void,
  handleError?: () => void
) => {
  const endpoint = `${url}api/v1/pension/Payment/schedule/payments/summary`
  const endpointWithQueryParams = paramBuilder(endpoint, {
    period,
    year,
    employerCode,
  })
  const call = axios.get(
    endpointWithQueryParams,
    {
      headers: {
        Accept: "text/plain",
      },
    }
  );

  toastWrapper(call, 'Generating bulk receipt summary...', (resp) => {
    if (resp.data.data && Array.isArray(resp.data.data) && resp.data.data.length > 0) {
      handleDone(resp.data.data);
      return resp.data.message || 'Summary generated successfully!';
    } else {
      handleError && handleError();
      return 'There is no closed schedule for the specified period and employer code';
    }
  }, 'Error generating summary!', undefined, handleError);
};
